import type { HttpClientInstance } from '@thehotelsnetwork/http-client';

import type { TeamMember } from '../types';

import {
  changeRole,
  teamInviteUser,
  teamInviteUserSendEmail,
  teamList,
  teamUserDetail,
  teamUserProperties,
  teamUserPropertyAdd,
  teamUserPropertyRemove,
  teamUserRemove,
  teamUserUnassignedProperties,
  teamTransferOwnership,
} from '@/api/routes/team';
import type { Property } from '@/modules/Properties/types';
import type { ResponseData } from '@/types';

// ============================================================================
// Dependency Types
// ============================================================================

export interface TeamApiParams {
  deps: {
    apiClient: HttpClientInstance;
  };
}

// ============================================================================
// Response Types
// ============================================================================

export type TeamMembersResponse = ResponseData<TeamMember[]>;

export type TeamMemberPropertiesResponse = ResponseData<Property[]>;

// ============================================================================
// Request Parameter Types
// ============================================================================

export interface GetTeamMembersParams extends TeamApiParams {
  accountId: string;
}

export interface GetTeamMemberDetailParams extends TeamApiParams {
  accountId: string;
  teamMemberId: string;
}

export interface GetTeamMemberPropertiesParams extends TeamApiParams {
  accountId: string;
  teamMemberId: string;
  siteType?: string;
}

export interface GetUnassignedPropertiesParams extends TeamApiParams {
  accountId: string;
  teamMemberId: string;
}

export interface RemoveTeamMemberParams extends TeamApiParams {
  accountId: string;
  teamMemberId: string;
}

export interface ChangeTeamMemberRoleParams extends TeamApiParams {
  accountId: string;
  teamMemberId: string;
  role: string;
}

export interface AddPropertyToTeamMemberParams extends TeamApiParams {
  accountId: string;
  teamMemberId: string;
  propertyId: string;
}

export interface RemovePropertyFromTeamMemberParams extends TeamApiParams {
  accountId: string;
  teamMemberId: string;
  propertyId: string;
}

export interface InviteMemberPayload {
  email: string;
  role: string;
  allProperties: boolean;
  propertyIds: string[];
  sendEmail: boolean;
}

export interface InviteTeamMemberParams extends TeamApiParams {
  accountId: string;
  invitation: InviteMemberPayload;
}

export interface ResendInvitationEmailParams extends TeamApiParams {
  accountId: string;
  teamMemberId: string;
}

export interface TransferTeamOwnershipParams extends TeamApiParams {
  accountId: string;
  teamMemberId: string;
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Get list of team members for an account
 */
export async function getTeamMembers(
  params: GetTeamMembersParams,
): Promise<TeamMembersResponse> {
  const { accountId, deps } = params;

  const { data } = await deps.apiClient.request<TeamMembersResponse>({
    method: 'GET',
    url: teamList,
    pathParams: {
      id: accountId,
    },
    params: {
      per_page: 1000, // Hardcoded trying to fetch all records in a single call
      page_num: 1,
    },
  });

  return data;
}

/**
 * Get details of a specific team member
 */
export async function getTeamMemberDetail(
  params: GetTeamMemberDetailParams,
): Promise<TeamMember> {
  const { accountId, teamMemberId, deps } = params;

  const { data } = await deps.apiClient.request<TeamMember>({
    method: 'GET',
    url: teamUserDetail,
    pathParams: {
      id: accountId,
      teamMemberId,
    },
  });

  return data;
}

/**
 * Get properties assigned to a team member
 */
export async function getTeamMemberProperties(
  params: GetTeamMemberPropertiesParams,
): Promise<TeamMemberPropertiesResponse> {
  const {
    accountId,
    teamMemberId,
    siteType = 'hotel',
    deps,
  } = params;

  const { data } = await deps.apiClient.request<TeamMemberPropertiesResponse>({
    method: 'GET',
    url: teamUserProperties,
    pathParams: {
      id: accountId,
      teamMemberId,
    },
    params: {
      // (10/07/2024) set to 3000 because is not paginated and one user has more than 1000 properties
      per_page: 3000, // Hardcoded trying to fetch all records in a single call
      page_num: 1,
      'filters[0][field]': 'site_type',
      'filters[0][operator]': 'eq',
      'filters[0][value]': siteType,
    },
  });

  return data;
}

/**
 * Get unassigned properties for a team member
 */
export async function getUnassignedProperties(
  params: GetUnassignedPropertiesParams,
): Promise<Property[]> {
  const { accountId, teamMemberId, deps } = params;

  const { data } = await deps.apiClient.request<{ data: Property[] }>({
    method: 'GET',
    url: teamUserUnassignedProperties,
    pathParams: {
      id: accountId,
      teamMemberId,
    },
    params: {
      page_num: 1,
      per_page: 1000, // Hardcoded trying to fetch all records in a single call
    },
  });

  return data.data;
}

/**
 * Remove a team member from the account
 */
export async function removeTeamMember(
  params: RemoveTeamMemberParams,
): Promise<void> {
  const { accountId, teamMemberId, deps } = params;

  await deps.apiClient.request({
    method: 'DELETE',
    url: teamUserRemove,
    pathParams: {
      account: accountId,
      id: teamMemberId,
    },
  });
}

/**
 * Change a team member's role
 */
export async function changeTeamMemberRole(
  params: ChangeTeamMemberRoleParams,
): Promise<void> {
  const {
    accountId,
    teamMemberId,
    role,
    deps,
  } = params;

  await deps.apiClient.request({
    method: 'PUT',
    url: changeRole,
    pathParams: {
      account: accountId,
      id: teamMemberId,
    },
    data: {
      role_name: role,
    },
  });
}

/**
 * Add a property to a team member
 */
export async function addPropertyToTeamMember(
  params: AddPropertyToTeamMemberParams,
): Promise<void> {
  const {
    accountId,
    teamMemberId,
    propertyId,
    deps,
  } = params;

  await deps.apiClient.request({
    method: 'POST',
    url: teamUserPropertyAdd,
    pathParams: {
      account: accountId,
      property_id: propertyId,
    },
    data: {
      team_member_id: teamMemberId,
    },
  });
}

/**
 * Remove a property from a team member
 */
export async function removePropertyFromTeamMember(
  params: RemovePropertyFromTeamMemberParams,
): Promise<void> {
  const {
    accountId,
    teamMemberId,
    propertyId,
    deps,
  } = params;

  await deps.apiClient.request({
    method: 'DELETE',
    url: teamUserPropertyRemove,
    pathParams: {
      id: accountId,
      property_id: propertyId,
      team_member_id: teamMemberId,
    },
  });
}

/**
 * Invite a new team member
 */
export async function inviteTeamMember(
  params: InviteTeamMemberParams,
): Promise<void> {
  const { accountId, invitation, deps } = params;

  await deps.apiClient.request({
    method: 'POST',
    url: teamInviteUser,
    pathParams: {
      account: accountId,
    },
    data: {
      email: invitation.email,
      role: invitation.role,
      all_properties: invitation.allProperties,
      property_ids: invitation.propertyIds,
      send_email: invitation.sendEmail,
    },
  });
}

/**
 * Resend invitation email to a team member
 */
export async function resendInvitationEmail(
  params: ResendInvitationEmailParams,
): Promise<TeamMember> {
  const { accountId, teamMemberId, deps } = params;

  const { data } = await deps.apiClient.request<{ data: TeamMember }>({
    method: 'POST',
    url: teamInviteUserSendEmail,
    pathParams: {
      account: accountId,
      teamMemberId,
    },
  });

  return data.data;
}

export async function transferTeamOwnership(
  params: TransferTeamOwnershipParams,
): Promise<void> {
  const { accountId, teamMemberId, deps } = params;

  await deps.apiClient.request({
    method: 'PUT',
    url: teamTransferOwnership,
    pathParams: {
      account: accountId,
    },
    data: {
      external_user_id: teamMemberId,
    },
  });
}
